import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

export default function ManageClientForm({ client, onClose, onClientUpdated }) {
  const [form, setForm] = useState({
    id: client.id,
    fullName: client.fullName || "",
    email: client.email || "",
    phone: client.phone || "",
    joined: client.joined || "",
    startingBalance: client.startingBalance || 0,
    currentBalance: client.currentBalance || 0,
    feePaid: client.feePaid || false,
    status: client.status || "Inactive",
  });

  // Fetch latest currentBalance from Firestore
  useEffect(() => {
    const fetchCurrentBalance = async () => {
      try {
        const userRef = doc(db, "users", client.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setForm((prev) => ({
            ...prev,
            currentBalance: userSnap.data().currentBalance || prev.startingBalance,
          }));
        }
      } catch (err) {
        console.error("Error fetching currentBalance:", err);
      }
    };

    fetchCurrentBalance();
  }, [client.id]);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  const handleSave = async () => {
    try {
      const docRef = doc(db, "users", form.id);
      const updatedData = {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        joined: form.joined,
        startingBalance: parseFloat(form.startingBalance),
        currentBalance: parseFloat(form.currentBalance),
        feePaid: form.feePaid,
        status: form.status,
      };

      await updateDoc(docRef, updatedData);
      onClientUpdated({ ...form }); // Trigger state update in parent
    } catch (error) {
      alert("Error updating client.");
      console.error(error);
    }
  };

  const badgeBase = {
    padding: "0.2rem 0.6rem",
    borderRadius: "999px",
    fontSize: "0.75rem",
    fontWeight: "bold",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  const getBadgeStyle = (status) => {
    switch (status) {
      case "Active":
        return { backgroundColor: "#dbeafe", color: "#1e40af" };
      case "Inactive":
        return { backgroundColor: "#f3f4f6", color: "#6b7280" };
      case "Processing":
        return { backgroundColor: "#fef9c3", color: "#92400e" };
      case "Blocked":
        return { backgroundColor: "#fee2e2", color: "#991b1b" };
      default:
        return {};
    }
  };

  return (
    <div style={panelStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3>Manage Client: {form.fullName}</h3>
        <button onClick={onClose} style={{ fontSize: "1.2rem", background: "none", border: "none", cursor: "pointer" }}>
          ✕
        </button>
      </div>

      <p style={{ color: "#6b7280", marginBottom: "1rem" }}>
        View/update details or add transactions. Current Balance:{" "}
        <strong>
          $
          {parseFloat(form.currentBalance).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </strong>
      </p>

      <label>Client ID</label>
      <input value={form.id} disabled style={inputStyle} />

      <label>Name</label>
      <input
        value={form.fullName}
        onChange={(e) => handleChange("fullName", e.target.value)}
        style={inputStyle}
      />

      <label>Email</label>
      <input
        value={form.email}
        onChange={(e) => handleChange("email", e.target.value)}
        style={inputStyle}
      />

      <label>Phone</label>
      <input
        value={form.phone}
        onChange={(e) => handleChange("phone", e.target.value)}
        style={inputStyle}
      />

      <label>Joined</label>
      <input
        value={form.joined}
        disabled
        style={{ ...inputStyle, backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
      />

      <label>Current Balance</label>
      <input
        type="number"
        value={form.currentBalance}
        disabled
        style={{ ...inputStyle, backgroundColor: "#f3f4f6", cursor: "not-allowed" }}
      />

      {/* Fee Paid Switch */}
      <div style={toggleRow}>
        <label>Fee Paid</label>
        <label className="switch">
          <input
            type="checkbox"
            checked={form.feePaid}
            onChange={() => handleChange("feePaid", !form.feePaid)}
          />
          <span className="slider round"></span>
        </label>
        <span style={{ fontSize: "0.8rem", marginLeft: "0.5rem" }}>
          {form.feePaid ? "✓ Paid" : "✘ Not Paid"}
        </span>
      </div>

      {/* Status Dropdown */}
      <div style={{ marginBottom: "1rem" }}>
        <label>Status</label>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.3rem" }}>
          <select
            value={form.status}
            onChange={(e) => handleChange("status", e.target.value)}
            style={{
              padding: "0.5rem",
              borderRadius: "6px",
              border: "1px solid #ccc",
              flex: "1",
            }}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Processing">Processing</option>
            <option value="Blocked">Blocked</option>
          </select>
          <span style={{ ...badgeBase, ...getBadgeStyle(form.status) }}>
            {form.status}
          </span>
        </div>
      </div>

      <hr style={{ margin: "1.5rem 0" }} />

      <div style={{ marginTop: "2rem", display: "flex", justifyContent: "space-between" }}>
        <button onClick={onClose} style={cancelBtn}>
          Cancel
        </button>
        <button onClick={handleSave} style={saveBtn}>
          Save Client Changes
        </button>
      </div>

      <style>
        {`
        .switch {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 20px;
        }

        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.4s;
          border-radius: 20px;
        }

        .slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: 0.4s;
          border-radius: 50%;
        }

        input:checked + .slider {
          background-color: #1e40af;
        }

        input:checked + .slider:before {
          transform: translateX(20px);
        }
      `}
      </style>
    </div>
  );
}

// Styles
const panelStyle = {
  position: "fixed",
  top: 0,
  right: 0,
  width: "480px",
  height: "100%",
  background: "#fff",
  boxShadow: "-2px 0 8px rgba(0,0,0,0.1)",
  padding: "2rem",
  overflowY: "auto",
  zIndex: 1000,
};

const inputStyle = {
  width: "100%",
  padding: "0.6rem",
  marginBottom: "1rem",
  borderRadius: "6px",
  border: "1px solid #ccc",
};

const toggleRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "1rem",
};

const cancelBtn = {
  backgroundColor: "#e5e7eb",
  color: "#374151",
  padding: "0.7rem 1.4rem",
  borderRadius: "4px",
  border: "none",
  cursor: "pointer",
};

const saveBtn = {
  backgroundColor: "#1e40af",
  color: "white",
  padding: "0.7rem 1.4rem",
  borderRadius: "4px",
  border: "none",
  cursor: "pointer",
};
